import { FC, PropsWithChildren, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';

import { Box, TouchableBox } from '@/components/lib/components';
import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';
import { Modal } from '@/feature/alerts/components/Modal';
import { isWeb } from '@/utils/constants-platform-specific';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';

import { RaceToPlaceSheet } from './RaceToPlaceSheet';

export type RaceToPlaceSheet = {
    showRaceToPlaceSheet: () => void;
    dismissRaceToPlaceSheet: () => void;
};

export const RaceToPlaceSheetContext = createContext<RaceToPlaceSheet>({
    showRaceToPlaceSheet: () => {},
    dismissRaceToPlaceSheet: () => {},
});

export const useRaceToPlaceSheet = () => {
    const context = useContext(RaceToPlaceSheetContext);
    if (!context) {
        throw new Error('useRaceToPlaceSheet must be used within a RaceToPlaceSheetProvider');
    }
    return context;
};

export const RaceToPlaceSheetProvider: FC<PropsWithChildren> = ({ children }) => {
    const raceToPlaceSheetRef = useRef<BottomSheetModalMethods | null>(null);
    const [isPlacingEntry, setIsPlacingEntry] = useState(false);

    const raceToPlaceSheet = useMemo(
        () => ({
            showRaceToPlaceSheet: () => {
                raceToPlaceSheetRef.current?.present();
            },
            dismissRaceToPlaceSheet: () => {
                raceToPlaceSheetRef.current?.dismiss();
            },
        }),
        []
    );

    const handleDismissPress = useCallback(() => {
        if (!isPlacingEntry) {
            raceToPlaceSheet.dismissRaceToPlaceSheet();
        }
    }, [isPlacingEntry, raceToPlaceSheet]);

    return (
        <CustomBottomSheetModalProvider>
            <RaceToPlaceSheetContext.Provider value={raceToPlaceSheet}>
                {children}
                <Box zIndex={2}>
                    {/* Full-screen transparent modal
                        A normal dynamic-height modal would resize itself as content changes, causing the sheet
                        to shift up and down, making the content appear unpinned from the bottom.
                        Instead, we use a transparent full-screen modal and handle layout and animations ourselves. */}
                    <Modal
                        id="race-to-place-sheet"
                        modalRef={raceToPlaceSheetRef}
                        handleComponent={NoHandle}
                        hideCloseButtonOnWeb
                        disableScrollContainerOnWeb
                        backgroundColor={'transparent'}
                        snapPoints={['100%']}
                        enableDynamicSizing={false}
                        hasBottomSheetView={false}
                        dismissible={!isPlacingEntry}
                        enableOverDrag={!isPlacingEntry}
                        disableFullWindowOverlay
                    >
                        {isWeb ? (
                            <RaceToPlaceSheet
                                handleClosePress={handleDismissPress}
                                showLoadingOverlay={isPlacingEntry}
                                setShowLoadingOverlay={setIsPlacingEntry}
                            />
                        ) : (
                            <Box justifyContent={'flex-end'} style={StyleSheet.absoluteFill}>
                                <TouchableBox flex={1} backgroundColor={'transparent'} onPress={handleDismissPress} />
                                <RaceToPlaceSheet
                                    handleClosePress={handleDismissPress}
                                    showLoadingOverlay={isPlacingEntry}
                                    setShowLoadingOverlay={setIsPlacingEntry}
                                />
                            </Box>
                        )}
                    </Modal>
                </Box>
            </RaceToPlaceSheetContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

const NoHandle = () => null;
